import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, MoreVertical, Check, X, Clock, User, Mail, Phone, Calendar, Users, Plus, Maximize2, Minimize2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const Bookings = () => {
  const { user, token } = useAuth();
  const [business, setBusiness] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Manual booking dialog
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [creatingBooking, setCreatingBooking] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    service_ids: [],
    staff_id: "",
    calendar_id: "",
    start_datetime: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      const bizResponse = await axios.get(`${API}/businesses`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      if (bizResponse.data.length > 0) {
        const biz = bizResponse.data[0];
        setBusiness(biz);

        const params = filter !== "all" ? `?status=${filter}` : "";
        const [bookingsResponse, servicesResponse, staffResponse, calendarsResponse] = await Promise.all([
          axios.get(`${API}/businesses/${biz.business_id}/bookings${params}`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }),
          axios.get(`${API}/businesses/${biz.business_id}/services`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }),
          axios.get(`${API}/businesses/${biz.business_id}/staff`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }),
          axios.get(`${API}/businesses/${biz.business_id}/calendars`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          })
        ]);
        
        setBookings(bookingsResponse.data);
        setServices(servicesResponse.data);
        setStaff(staffResponse.data || []);
        setCalendars(calendarsResponse.data || []);
      }
    } catch (error) {
      toast.error("Errore nel caricamento delle prenotazioni");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(`Prenotazione ${status === 'confirmed' ? 'confermata' : status === 'cancelled' ? 'cancellata' : 'aggiornata'}!`);
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'aggiornamento");
    }
  };

  // Get staff filtered by selected services
  const getAvailableStaff = () => {
    if (bookingForm.service_ids.length === 0) return staff;
    return staff.filter(s => 
      s.services?.length === 0 || // Staff with no specific services can do all
      s.services?.some(svcId => bookingForm.service_ids.includes(svcId))
    );
  };

  const handleServiceToggle = (serviceId) => {
    setBookingForm(prev => ({
      ...prev,
      service_ids: prev.service_ids.includes(serviceId)
        ? prev.service_ids.filter(id => id !== serviceId)
        : [...prev.service_ids, serviceId],
      staff_id: "" // Reset staff when services change
    }));
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    if (bookingForm.service_ids.length === 0) {
      toast.error("Seleziona almeno un servizio");
      return;
    }
    if (!bookingForm.start_datetime) {
      toast.error("Seleziona data e ora");
      return;
    }
    
    setCreatingBooking(true);
    try {
      await axios.post(`${API}/admin/businesses/${business.business_id}/bookings`, bookingForm, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Prenotazione creata con successo!");
      setBookingDialogOpen(false);
      setBookingForm({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        service_ids: [],
        staff_id: "",
        calendar_id: "",
        start_datetime: "",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione della prenotazione");
    } finally {
      setCreatingBooking(false);
    }
  };

  const getServiceName = (serviceId) => {
    return services.find(s => s.service_id === serviceId)?.name || "Servizio";
  };

  const getServicesDisplay = (booking) => {
    // Handle both old (single service_id) and new (service_ids array) format
    if (booking.services_details && booking.services_details.length > 0) {
      return booking.services_details;
    }
    
    const serviceIds = booking.service_ids || (booking.service_id ? [booking.service_id] : []);
    return serviceIds.map(id => {
      const service = services.find(s => s.service_id === id);
      return service ? { name: service.name, duration_minutes: service.duration_minutes, price: service.price } : { name: id };
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-800 hover:bg-amber-100",
      confirmed: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
      cancelled: "bg-red-100 text-red-800 hover:bg-red-100",
      completed: "bg-blue-100 text-blue-800 hover:bg-blue-100"
    };
    const labels = {
      pending: "In Attesa",
      confirmed: "Confermata",
      cancelled: "Cancellata",
      completed: "Completata"
    };
    return <Badge className={styles[status] || styles.pending}>{labels[status] || status}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-800",
      paid: "bg-emerald-100 text-emerald-800",
      not_required: "bg-slate-100 text-slate-600",
      refunded: "bg-purple-100 text-purple-800"
    };
    const labels = {
      pending: "Da Pagare",
      paid: "Pagato",
      not_required: "N/A",
      refunded: "Rimborsato"
    };
    return <Badge variant="outline" className={styles[status]}>{labels[status] || status}</Badge>;
  };

  const filteredBookings = bookings.filter(booking => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      booking.customer_name?.toLowerCase().includes(searchLower) ||
      booking.customer_email?.toLowerCase().includes(searchLower) ||
      booking.customer_phone?.includes(search)
    );
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!business) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Crea prima la tua attività dalla Dashboard</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className={`space-y-6 ${isFullscreen ? 'fixed inset-0 z-50 bg-white p-6 overflow-auto' : ''}`} data-testid="bookings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Prenotazioni
            </h1>
            <p className="text-slate-500 mt-1">Gestisci le prenotazioni dei tuoi clienti</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsFullscreen(!isFullscreen)}>
              {isFullscreen ? <Minimize2 className="h-4 w-4 mr-2" /> : <Maximize2 className="h-4 w-4 mr-2" />}
              {isFullscreen ? 'Esci' : 'Schermo Intero'}
            </Button>
            <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-booking-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuova Prenotazione
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Crea Prenotazione Manuale</DialogTitle>
                  <DialogDescription>Aggiungi una prenotazione direttamente dal pannello</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateBooking} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nome Cliente *</Label>
                    <Input 
                      value={bookingForm.customer_name} 
                      onChange={(e) => setBookingForm({...bookingForm, customer_name: e.target.value})}
                      placeholder="Mario Rossi"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input 
                        type="email"
                        value={bookingForm.customer_email} 
                        onChange={(e) => setBookingForm({...bookingForm, customer_email: e.target.value})}
                        placeholder="mario@email.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefono</Label>
                      <Input 
                        value={bookingForm.customer_phone} 
                        onChange={(e) => setBookingForm({...bookingForm, customer_phone: e.target.value})}
                        placeholder="+39 333 1234567"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Servizi *</Label>
                    <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                      {services.filter(s => s.is_active).map(service => (
                        <div key={service.service_id} className="flex items-center gap-2">
                          <Checkbox 
                            id={service.service_id}
                            checked={bookingForm.service_ids.includes(service.service_id)}
                            onCheckedChange={() => handleServiceToggle(service.service_id)}
                          />
                          <label htmlFor={service.service_id} className="text-sm cursor-pointer flex-1">
                            {service.name} <span className="text-slate-500">({service.duration_minutes} min - €{service.price})</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Operatore</Label>
                    <Select value={bookingForm.staff_id || "none"} onValueChange={(val) => setBookingForm({...bookingForm, staff_id: val === "none" ? "" : val})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona operatore (opzionale)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessuno</SelectItem>
                        {getAvailableStaff().map(s => (
                          <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {bookingForm.service_ids.length > 0 && getAvailableStaff().length === 0 && (
                      <p className="text-xs text-amber-600">Nessuno staff disponibile per i servizi selezionati</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Calendario</Label>
                    <Select value={bookingForm.calendar_id} onValueChange={(val) => setBookingForm({...bookingForm, calendar_id: val})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona calendario" /></SelectTrigger>
                      <SelectContent>
                        {calendars.map(c => (
                          <SelectItem key={c.calendar_id} value={c.calendar_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Data e Ora *</Label>
                    <Input 
                      type="datetime-local"
                      value={bookingForm.start_datetime}
                      onChange={(e) => setBookingForm({...bookingForm, start_datetime: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Note</Label>
                    <Input 
                      value={bookingForm.notes}
                      onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                      placeholder="Note aggiuntive..."
                    />
                  </div>
                  
                  <DialogFooter>
                    <Button type="submit" disabled={creatingBooking}>
                      {creatingBooking ? "Creazione..." : "Crea Prenotazione"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-slate-100">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Cerca per nome, email o telefono..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="bookings-search"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="bookings-filter">
                  <SelectValue placeholder="Filtra per stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="pending">In Attesa</SelectItem>
                  <SelectItem value="confirmed">Confermate</SelectItem>
                  <SelectItem value="completed">Completate</SelectItem>
                  <SelectItem value="cancelled">Cancellate</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Bookings Table */}
        <Card className="border-slate-100">
          <CardContent className="p-0">
            {filteredBookings.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-slate-500">Nessuna prenotazione trovata</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Servizi</TableHead>
                      <TableHead>Data & Ora</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Pagamento</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBookings.map((booking) => {
                      const servicesDisplay = getServicesDisplay(booking);
                      return (
                        <TableRow key={booking.booking_id} data-testid={`booking-row-${booking.booking_id}`}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-slate-400" />
                                <span className="font-medium">{booking.customer_name}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Mail className="h-3 w-3" />
                                <span className="truncate max-w-[200px]">{booking.customer_email}</span>
                              </div>
                              {booking.customer_phone && (
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                  <Phone className="h-3 w-3" />
                                  <span>{booking.customer_phone}</span>
                                </div>
                              )}
                              {booking.num_attendees > 1 && (
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                  <Users className="h-3 w-3" />
                                  <span>{booking.num_attendees} partecipanti</span>
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {servicesDisplay.map((svc, idx) => (
                                <div key={idx} className="text-sm">
                                  <span className="font-medium">{svc.name}</span>
                                  {svc.duration_minutes && (
                                    <span className="text-slate-500 ml-1">({svc.duration_minutes} min)</span>
                                  )}
                                </div>
                              ))}
                              {booking.total_duration_minutes > 0 && servicesDisplay.length > 1 && (
                                <div className="text-xs text-slate-400 pt-1 border-t mt-1">
                                  Totale: {booking.total_duration_minutes} min
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-slate-400" />
                                <span>{format(new Date(booking.start_datetime?.replace(/Z$/, '')), "dd MMM yyyy", { locale: it })}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Clock className="h-3 w-3" />
                                <span>
                                  {format(new Date(booking.start_datetime?.replace(/Z$/, '')), "HH:mm")} - {format(new Date(booking.end_datetime?.replace(/Z$/, '')), "HH:mm")}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(booking.status)}</TableCell>
                          <TableCell>
                            {getPaymentBadge(booking.payment_status)}
                            {booking.payment_amount > 0 && (
                              <span className="text-sm text-slate-500 ml-2">
                                €{booking.payment_amount.toFixed(2)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`booking-menu-${booking.booking_id}`}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {booking.status === "pending" && (
                                  <DropdownMenuItem onClick={() => updateStatus(booking.booking_id, "confirmed")}>
                                    <Check className="h-4 w-4 mr-2 text-emerald-600" />
                                    Conferma
                                  </DropdownMenuItem>
                                )}
                                {booking.status === "confirmed" && (
                                  <DropdownMenuItem onClick={() => updateStatus(booking.booking_id, "completed")}>
                                    <Check className="h-4 w-4 mr-2 text-blue-600" />
                                    Completa
                                  </DropdownMenuItem>
                                )}
                                {booking.status !== "cancelled" && booking.status !== "completed" && (
                                  <DropdownMenuItem 
                                    className="text-red-600"
                                    onClick={() => updateStatus(booking.booking_id, "cancelled")}
                                  >
                                    <X className="h-4 w-4 mr-2" />
                                    Cancella
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Bookings;
